import {Inject, Injectable} from '@angular/core';
import {Session} from '../model/session';
import {DOCUMENT, Location} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {ObNotificationService, ObPopUpService} from '@oblique/oblique';
import {RequestsService} from '../requests/requests.service';
import {OidcClientWrapper} from '../wasm/oidc_client.service';
import {SessionEncryptionWrapper} from '../wasm/session_encryption.service';
import {Keypair} from '../model/keypair';
import {VerificationService} from '../verification/verification.service';
import {OidcParams} from '../model/oidc_params';
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {environment} from "../../../environments/environment";
import {Subscription} from "rxjs";

@Injectable({
	providedIn: 'root'
})
export class LoginService {
	readonly name?: string | null;

	private _idToken?: string | null;
	private session?: Session | null;

	private readonly MMMI_SESSION = 'mmmi_session';
	private readonly MMMI_ID_TOKEN = 'id_token';
	private readonly MMMI_LAST_FETCHED = 'mmmi_last_fetched';
	private readonly MMMI_DISPLAY_NAME = 'display_name';
	private readonly MMMI_USER_NAME = 'user_name';

	private errorSubscription : Subscription | null = null

	constructor(
		// DOM interactions
		@Inject(DOCUMENT) private readonly document: Document,
		@Inject(ObNotificationService) private notificationSerivce : ObNotificationService,
		@Inject(TranslateService) private translationService : TranslateService,
		private readonly location: Location,
		private readonly route: ActivatedRoute,
		private readonly popupService: ObPopUpService,

		// requesting logic
		private readonly requestsService: RequestsService,

		// WASM component wrappers
		private readonly oidcClient: OidcClientWrapper,
		private readonly sessionEncryption: SessionEncryptionWrapper
	) {
		this._idToken = sessionStorage.getItem(this.MMMI_ID_TOKEN);
		this.name = sessionStorage.getItem(this.MMMI_DISPLAY_NAME);

		const sessionString = sessionStorage.getItem(this.MMMI_SESSION);
		this.session = sessionString != null ? JSON.parse(sessionString) : null;

		void this.init();
	}

	get id(): string | undefined {
		return this.session?.id;
	}

	get apiToken(): string | undefined {
		return this.session?.apiToken;
	}

	get idToken() {
		return this._idToken;
	}

	get keypair(): Keypair | undefined {
		return this.session?.keypair;
	}

	get expiry(): number | undefined {
		return this.session?.expiry;
	}

	async login(): Promise<void> {
		const authUrl = await this.oidcClient.generate_auth_url();
		sessionStorage.removeItem(this.MMMI_ID_TOKEN);
		this.document.location.href = authUrl;
	}

	async logout() {
		if (
			this.popupService.confirm(
				'Are you sure you wish to log out from your current session?\nThis action will delete all previous verification requests and abort any outstanding ones.\nDo you want to proceed?'
			)
		) {
			let logoutUrl = undefined;
			try {
				logoutUrl = await this.oidcClient.logout_url(environment.redirect_url);
			}catch (e) {
				console.error(e)
			}
			sessionStorage.removeItem(this.MMMI_SESSION);
			sessionStorage.removeItem(this.MMMI_ID_TOKEN);
			sessionStorage.removeItem(this.MMMI_LAST_FETCHED);
			sessionStorage.removeItem(this.MMMI_DISPLAY_NAME);
			sessionStorage.removeItem(this.MMMI_USER_NAME);
			VerificationService.dropEverything();
			if(logoutUrl != null) {
				window.location.href = logoutUrl;
			} else {
				window.location.reload();
			}
		}
	}

	async hash(jwt: string): Promise<string> {
		const msgUint8 = new TextEncoder().encode(jwt); // encode as (utf-8) Uint8Array
		const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the message
		const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
		const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
		return hashHex;
	}

	//TODO: not duplicate code
	private parseAcr(acr: string | undefined) : number|undefined {
		if(acr == null) {
			return 0;
		}
		// urn:qoa.eiam.admin.ch:names:tc:ac:classes:30
		const values = acr.split(':');
		const acrValue = values.at(-1)
		if (acrValue == null) {
			return 0
		}
		return parseInt(acrValue);
	}

	// TODO: Handle expired JWT case - automatically refresh and re-register with MMMI server
	// TODO: Handle expired session case - automatically refresh and display new user ID
	private async init() {
		sessionStorage.removeItem("ACR_ERROR");
		if (this.session == null || (this.session != null && this.session.expiry < Date.now())) {
			// no valid registration with MMMI server
			if (
				this._idToken == null ||
				(await this.oidcClient
					.decode_token(this._idToken)
					.then((token) => {
						const claims = JSON.parse(token);
						let acr = this.parseAcr(claims.acr);
						if(acr == null) {
							return true;
						}
						if (acr < environment.minimum_acr) {
							this._idToken = null;
						}
						return acr < environment.minimum_acr;

					})
					.catch(() => true))
			) {
				// no valid OIDC id token
				this.route.queryParams.subscribe(params => {
					const authCode = (params as OidcParams).code;
					const state = (params as OidcParams).state;
					const error = (params as OidcParams).error;
					if(error != null && error == "vips_needed"){
						this.notificationSerivce.error("i18n.mmmi.login.acr")
						return;
					}
					if (authCode != null && state != null) {
						this.location.replaceState(this.location.path().split('?')[0], ''); // reset URL parameters
						this.handleLoginRedirect(authCode, state);
					} else {
						// else do nothing - wait for user to request login
						console.info('waiting for user to commence login process');
						this._idToken = null;
					}
				});
			} else {
				// valid OIDC token - just re-register
				this.register(this._idToken)
					.then(() => console.info('successfully registered MMMI session'))
					.catch(reason => console.error('failed to register with backend server: ', reason));
			}
		} else {
			// user logged in, refresh token
			this.route.queryParams.subscribe(params => {
				const authCode = (params as OidcParams).code;
				const state = (params as OidcParams).state;
				if (authCode != null && state != null) {
					this.location.replaceState(this.location.path().split('?')[0], ''); // reset URL parameters
					this.handleLoginRedirect(authCode, state);
				}
			});
		}
	}

	private handleLoginRedirect(authCode: string, state: string) {
		// arrived here after logging in at OIDC server - fetch id token and register with MMMI server
		this.fetchIdToken(authCode, state)
			.then(async idToken => {
				this._idToken = idToken;
				let result = await this.oidcClient.decode_token(this._idToken).then(token => {
					let claims = JSON.parse(token);
					let acr = this.parseAcr(claims.acr);
					if(acr == null) {
						return true;
					}
					if (acr < environment.minimum_acr) {
						this._idToken = null;
					}
					return acr >= environment.minimum_acr;
				}).catch(
					reason => {
						console.error('acr level too low');
						return false
					}
				);
				if(result) {
					// if login succeeded, register with backend server
					this.register(this._idToken)
						.then(() => console.info('successfully registered MMMI session'))
						.catch(reason => console.error('failed to register with backend server: ', reason));
				} else {
					if(sessionStorage.getItem("ACR_ERROR")) {
						// we already notified the user
						return;
					}
					sessionStorage.setItem("ACR_ERROR", "error set");

					this.notificationSerivce.error("i18n.mmmi.login.acr")
				}
			})
			.catch(reason => {
				console.error('failed to fetch ID token: ', reason);
			});
	}

	private async fetchIdToken(authCode: string, state: string): Promise<string> {
		const idToken = await this.oidcClient.get_token(authCode, state);
		return JSON.parse(idToken).id_token;
	}

	private async register(idToken: string) {
		// generate keypair
		const keypair: Keypair = JSON.parse(await this.sessionEncryption.create_key_pair());
		const publicKey = keypair.publicKey;
		const privateKey = keypair.privateKey;

		// compute JWT hash
		const jwtHash = await this.hash(idToken);

		// register public key and JWT hash
		this.requestsService.register({publicKey, jwtHash}).subscribe(async response => {
			// make sure the expiry is not over the jwt lifetime
			let tokenExpiry = JSON.parse((await this.oidcClient.decode_token(idToken))).exp * 1000

			this.session = {
				id: response.id,
				apiToken: response.apiToken,
				expiry: Math.min(response.expiry, tokenExpiry),
				keypair: {publicKey, privateKey},
				outstandingRequests: [],
				handledRequests: []
			};

			// store session
			sessionStorage.setItem(this.MMMI_SESSION, JSON.stringify(this.session));
		});
	}
}
